import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, Calendar, ClipboardList, FileText, CheckCircle2,
  Clock, ChevronRight, AlertCircle, TrendingUp, BarChart3
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const ManagementReview = () => {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateReview, setShowCreateReview] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [newDecision, setNewDecision] = useState("");
  const [newActionItem, setNewActionItem] = useState({ description: "", owner: "", due_date: "" });

  const [newReview, setNewReview] = useState({
    title: "",
    review_date: "",
    attendees: [],
    agenda_items: [],
    notes: ""
  });
  const [attendeeInput, setAttendeeInput] = useState("");

  // S15b: default agenda items are now localised via the backend (DEFAULT_AGENDA_BY_LOCALE).
  // The frontend shows locale-appropriate item keys for preview only — the actual strings
  // come from the backend when the review is created.
  const defaultAgendaPreview = [
    t("mr.agenda.isms_performance"),
    t("mr.agenda.ca_status"),
    t("mr.agenda.changes"),
    t("mr.agenda.improvements"),
    t("mr.agenda.resources"),
    t("mr.agenda.risk_review"),
  ];

  const statuses = [
    { value: "scheduled", labelKey: "review.status.scheduled", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    { value: "in_progress", labelKey: "review.status.in_progress", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    { value: "completed", labelKey: "review.status.completed", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" }
  ];

  useEffect(() => {
    fetchReviews();
  }, [token]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/management-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewDetails = async (reviewId) => {
    try {
      const response = await axios.get(`${API}/management-reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedReview(response.data);
    } catch (error) {
      console.error("Failed to fetch review details:", error);
    }
  };

  const createReview = async () => {
    if (!newReview.title || !newReview.review_date) {
      toast.error(t("mr.toast.title_date_required"));
      return;
    }

    try {
      // S15b: pass locale so backend uses DEFAULT_AGENDA_BY_LOCALE[locale] when no agenda provided.
      // Pass agenda_items only if the user explicitly added items; otherwise let backend decide.
      await axios.post(
        `${API}/management-reviews?locale=${locale}`,
        {
          ...newReview,
          agenda_items: newReview.agenda_items.length > 0 ? newReview.agenda_items : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("mr.toast.created"));
      setShowCreateReview(false);
      setNewReview({ title: "", review_date: "", attendees: [], agenda_items: [], notes: "" });
      fetchReviews();
    } catch (error) {
      toast.error(extractError(error, t("mr.toast.create_failed")));
    }
  };

  const updateStatus = async (reviewId, status) => {
    try {
      await axios.put(`${API}/management-reviews/${reviewId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("mr.toast.status_updated"));
      fetchReviews();
      if (selectedReview?.review_id === reviewId) {
        fetchReviewDetails(reviewId);
      }
    } catch (error) {
      toast.error(extractError(error, t("mr.toast.status_failed")));
    }
  };

  const addDecision = async () => {
    if (!newDecision || !selectedReview) return;
    try {
      await axios.post(`${API}/management-reviews/${selectedReview.review_id}/decisions?decision=${encodeURIComponent(newDecision)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("mr.toast.decision_added"));
      setNewDecision("");
      fetchReviewDetails(selectedReview.review_id);
    } catch (error) {
      toast.error(extractError(error, t("mr.toast.decision_failed")));
    }
  };

  const addActionItem = async () => {
    if (!newActionItem.description || !selectedReview) return;
    try {
      let url = `${API}/management-reviews/${selectedReview.review_id}/action-items?description=${encodeURIComponent(newActionItem.description)}`;
      if (newActionItem.owner) url += `&owner=${encodeURIComponent(newActionItem.owner)}`;
      if (newActionItem.due_date) url += `&due_date=${newActionItem.due_date}`;

      await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("mr.toast.action_added"));
      setNewActionItem({ description: "", owner: "", due_date: "" });
      fetchReviewDetails(selectedReview.review_id);
    } catch (error) {
      toast.error(extractError(error, t("mr.toast.action_failed")));
    }
  };

  const addAttendee = () => {
    if (attendeeInput && !newReview.attendees.includes(attendeeInput)) {
      setNewReview({ ...newReview, attendees: [...newReview.attendees, attendeeInput] });
      setAttendeeInput("");
    }
  };

  const getStatusBadge = (status) => {
    return statuses.find(s => s.value === status)?.color || "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t("mr.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="management-review-page">
      <PageHeader
        title={t("mr.title")}
        description={t("mr.subtitle")}
        actions={
          <Button onClick={() => setShowCreateReview(true)} data-testid="create-review-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("mr.new")}
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {reviews.filter(r => r.status === "scheduled").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("review.status.scheduled")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {reviews.filter(r => r.status === "in_progress").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("review.status.in_progress")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {reviews.filter(r => r.status === "completed").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("review.status.completed")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      <ScrollArea className="h-[calc(100vh-24rem)]">
        <div className="space-y-3 pr-4">
          <AnimatePresence mode="popLayout">
            {reviews.map((review, index) => (
              <motion.div
                key={review.review_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
              >
                <Card
                  className="border-border hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => fetchReviewDetails(review.review_id)}
                  data-testid={`review-card-${review.review_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className={getStatusBadge(review.status)}>
                            {t(statuses.find(s => s.value === review.status)?.labelKey || review.status)}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {new Date(review.review_date).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="font-semibold mb-1">{review.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {review.attendees?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {review.attendees.length} {t("mr.attendees")}
                            </span>
                          )}
                          {review.action_items?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <ClipboardList className="w-4 h-4" />
                              {review.action_items.length} {t("mr.action_items_count")}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>

          {reviews.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t("review.no_reviews_title")}</p>
              <Button className="mt-4" onClick={() => setShowCreateReview(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t("mr.schedule_first")}
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Create Review Dialog */}
      <Dialog open={showCreateReview} onOpenChange={setShowCreateReview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("review.create_title")}</DialogTitle>
            <DialogDescription>
              {t("review.create_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>{t("review.title_label")} *</Label>
              <Input
                value={newReview.title}
                onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                placeholder={t("mr.title_placeholder")}
                data-testid="review-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("review.date_label")} *</Label>
              <Input
                type="date"
                value={newReview.review_date}
                onChange={(e) => setNewReview({ ...newReview, review_date: e.target.value })}
                data-testid="review-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("review.attendees_label")}</Label>
              <div className="flex gap-2">
                <Input
                  value={attendeeInput}
                  onChange={(e) => setAttendeeInput(e.target.value)}
                  placeholder={t("review.attendee_ph")}
                  onKeyPress={(e) => e.key === "Enter" && addAttendee()}
                />
                <Button type="button" variant="outline" onClick={addAttendee}>{t("add")}</Button>
              </div>
              {newReview.attendees.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newReview.attendees.map((a, i) => (
                    <Badge key={i} variant="secondary">
                      {a}
                      <button
                        className="ml-1 hover:text-destructive"
                        onClick={() => setNewReview({
                          ...newReview,
                          attendees: newReview.attendees.filter((_, idx) => idx !== i)
                        })}
                      >×</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t("mr.agenda_default_hint")}</Label>
              <div className="text-xs text-muted-foreground space-y-1">
                {defaultAgendaPreview.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateReview(false)}>{t("cancel")}</Button>
            <Button onClick={createReview} data-testid="save-review-btn">
              {t("review.create_btn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Details Dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {selectedReview && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={getStatusBadge(selectedReview.status)}>
                    {t(statuses.find(s => s.value === selectedReview.status)?.labelKey || selectedReview.status)}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {new Date(selectedReview.review_date).toLocaleDateString()}
                  </span>
                </div>
                <DialogTitle className="text-xl">{selectedReview.title}</DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Status Actions */}
                <div className="flex items-center gap-2">
                  <Select onValueChange={(v) => updateStatus(selectedReview.review_id, v)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={t("mr.change_status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* System Inputs Summary */}
                {selectedReview.inputs && (
                  <Card className="border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        {t("mr.system_inputs")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="p-3 rounded-lg bg-muted/50">
                          <p className="text-lg font-bold">{selectedReview.inputs.compliance_scores?.overall || 0}%</p>
                          <p className="text-xs text-muted-foreground">{t("mr.compliance_score")}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50">
                          <p className="text-lg font-bold">{selectedReview.inputs.open_ncs || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("mr.open_ncs")}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50">
                          <p className="text-lg font-bold">{selectedReview.inputs.recent_incidents || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("mr.recent_incidents")}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50">
                          <p className="text-lg font-bold">{selectedReview.inputs.kpi_summary?.assets_tracked || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("mr.assets_tracked")}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Agenda */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    {t("mr.agenda_label")}
                  </h4>
                  <div className="space-y-2">
                    {selectedReview.agenda_items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded bg-muted/30">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                          {idx + 1}
                        </div>
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Decisions */}
                <div>
                  <h4 className="font-semibold mb-3">{t("mr.decisions_made")}</h4>
                  <div className="space-y-2 mb-4">
                    {selectedReview.decisions?.map((d, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-muted/30">
                        <p className="text-sm">{d.decision}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {d.owner && `${t("mr.owner")}: ${d.owner} • `}
                          {new Date(d.recorded_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                    {(!selectedReview.decisions || selectedReview.decisions.length === 0) && (
                      <p className="text-sm text-muted-foreground">{t("mr.no_decisions")}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newDecision}
                      onChange={(e) => setNewDecision(e.target.value)}
                      placeholder={t("mr.add_decision_ph")}
                    />
                    <Button onClick={addDecision}>{t("add")}</Button>
                  </div>
                </div>

                <Separator />

                {/* Action Items */}
                <div>
                  <h4 className="font-semibold mb-3">{t("mr.action_items")}</h4>
                  <div className="space-y-2 mb-4">
                    {selectedReview.action_items?.map((a, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div>
                          <p className="text-sm font-medium">{a.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {a.owner && `${t("mr.owner")}: ${a.owner}`}
                            {a.due_date && ` • ${t("mr.due")}: ${new Date(a.due_date).toLocaleDateString()}`}
                          </p>
                        </div>
                        <Badge variant="secondary" className="capitalize">{a.status}</Badge>
                      </div>
                    ))}
                    {(!selectedReview.action_items || selectedReview.action_items.length === 0) && (
                      <p className="text-sm text-muted-foreground">{t("mr.no_action_items")}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={newActionItem.description}
                      onChange={(e) => setNewActionItem({ ...newActionItem, description: e.target.value })}
                      placeholder={t("mr.action_desc_ph")}
                    />
                    <div className="flex gap-2">
                      <Input
                        value={newActionItem.owner}
                        onChange={(e) => setNewActionItem({ ...newActionItem, owner: e.target.value })}
                        placeholder={t("mr.action_owner_ph")}
                        className="flex-1"
                      />
                      <Input
                        type="date"
                        value={newActionItem.due_date}
                        onChange={(e) => setNewActionItem({ ...newActionItem, due_date: e.target.value })}
                        className="w-40"
                      />
                      <Button onClick={addActionItem}>{t("add")}</Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementReview;
